import { Link, useRouterState } from "@tanstack/react-router";
import { detectCluster, getClusterTheme } from "@/lib/cluster";
import { useLocale } from "@/i18n/LocaleContext";
import { todayISO, parseISO, MONTH_SLUGS_ID, MONTH_NAMES_ID } from "@/lib/calendar/dates";
import { currentHijriYear, HIJRI_MONTHS, HIJRI_MONTHS_EN } from "@/lib/calendar/hijri";
import { getHariPentingForMonth } from "@/data/hari-penting";

/**
 * Cluster-aware footer block. Rendered above the global Footer to give
 * each topical cluster ≥10 contextual internal links (PRD goal:
 * topical authority + crawl depth ≤3 clicks).
 */
export function ClusterFooter() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const cluster = detectCluster(pathname);
  const { href, locale } = useLocale();
  if (!cluster) return null;
  const theme = getClusterTheme(cluster);

  const today = parseISO(todayISO())!;
  const y = today.y;
  const m = today.m;
  const hy = currentHijriYear();

  let title = "";
  let columns: Array<{ heading: string; links: Array<{ to: string; label: string }> }> = [];

  if (cluster === "calendar") {
    title = locale === "en" ? "Explore Calendar" : "Jelajahi Kalender";
    const months =
      locale === "en"
        ? ["January","February","March","April","May","June","July","August","September","October","November","December"]
        : MONTH_NAMES_ID;
    columns = [
      {
        heading: locale === "en" ? "Nearby Years" : "Tahun Terdekat",
        links: [
          ...[y - 2, y - 1, y + 1, y + 2].map((yy) => ({
            to: href(`/kalender/${yy}`),
            label: `Kalender ${yy}`,
          })),
          { to: href("/kalender-hijriah"), label: locale === "en" ? "Hijri Calendar" : "Kalender Hijriah" },
          { to: `/harpitnas/${y}/`, label: `Harpitnas ${y}` },
        ],
      },
      {
        heading: locale === "en" ? `Months ${y}` : `Bulan ${y}`,
        links: MONTH_SLUGS_ID.map((s, i) => ({
          to: href(`/kalender/${y}/${s}`),
          label: `${months[i]} ${y}`,
        })),
      },
      {
        heading: locale === "en" ? "Holidays & Red Dates" : "Libur & Tanggal Merah",
        links: [
          { to: href(`/tanggal-merah/${y}`), label: `Tanggal Merah ${y}` },
          { to: href(`/tanggal-merah/${y + 1}`), label: `Tanggal Merah ${y + 1}` },
          { to: href(`/libur-nasional/${y}`), label: `Libur Nasional ${y}` },
          { to: href(`/libur-nasional/${y + 1}`), label: `Libur Nasional ${y + 1}` },
          { to: href(`/cuti-bersama/${y}`), label: `Cuti Bersama ${y}` },
          { to: href(`/cuti-bersama/${y + 1}`), label: `Cuti Bersama ${y + 1}` },
        ],
      },
      {
        heading: locale === "en" ? "More" : "Lainnya",
        links: [
          { to: href("/hari-ini"), label: locale === "en" ? "Today" : "Hari Ini" },
          { to: href("/hari"), label: locale === "en" ? "Notable Days" : "Hari Penting" },
          { to: "/jadwal-sholat/", label: "Jadwal Sholat" },
          { to: href("/tools"), label: "Tools" },
          { to: "/zodiak/", label: "Zodiak" },
        ],
      },
    ];
  } else if (cluster === "hijri") {
    title = locale === "en" ? "Hijri & Ramadan" : "Hijriah & Ramadan";
    const hMonths = locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS;
    const hSuffix = locale === "en" ? "AH" : "H";
    columns = [
      {
        heading: locale === "en" ? "Hijri Year" : "Tahun Hijriah",
        links: [hy - 1, hy, hy + 1, hy + 2].map((yy) => ({
          to: href(`/kalender-hijriah/${yy}/`),
          label: `${yy} ${hSuffix}`,
        })),
      },
      {
        heading: locale === "en" ? "Hijri Months" : "Bulan Hijriah",
        links: hMonths.map((name, i) => ({
          to: href(`/kalender-hijriah/${hy}/${i + 1}/`),
          label: `${name} ${hy} ${hSuffix}`,
        })),
      },
      {
        heading: "Imsak & Buka Puasa",
        links: [
          { to: "/jadwal-imsak/", label: "Jadwal Imsakiyah" },
          { to: "/jadwal-buka-puasa/", label: "Jadwal Buka Puasa" },
          { to: "/jadwal-imsak/lokasi/", label: "Indeks Lokasi Imsak" },
          { to: "/jadwal-buka-puasa/lokasi/", label: "Indeks Lokasi Buka Puasa" },
          { to: "/jadwal-sholat/", label: "Jadwal Sholat" },
          { to: `/harpitnas/${y}/`, label: `Harpitnas ${y}` },
        ],
      },
      {
        heading: locale === "en" ? "More" : "Lainnya",
        links: [
          { to: href(`/kalender/${y}`), label: `Kalender ${y}` },
          { to: href("/hari"), label: locale === "en" ? "Notable Days" : "Hari Penting" },
          { to: href("/tools"), label: "Tools" },
          { to: "/zodiak/", label: "Zodiak" },
        ],
      },
    ];
  } else if (cluster === "prayer") {
    title = locale === "en" ? "Prayer Schedule" : "Jadwal Sholat";
    const cities: Array<[string, string]> = [
      ["dki-jakarta", "Jakarta"], ["bandung", "Bandung"], ["surabaya", "Surabaya"],
      ["medan", "Medan"], ["makassar", "Makassar"], ["yogyakarta", "Yogyakarta"],
      ["semarang", "Semarang"], ["denpasar", "Denpasar"], ["palembang", "Palembang"],
    ];
    columns = [
      {
        heading: locale === "en" ? "Quick Access" : "Akses Cepat",
        links: [
          { to: "/jadwal-sholat/", label: "Hari Ini" },
          { to: "/jadwal-sholat/lokasi/", label: "Indeks Lokasi" },
          { to: "/jadwal-imsak/", label: "Jadwal Imsakiyah" },
          { to: "/jadwal-buka-puasa/", label: "Buka Puasa" },
          { to: href(`/kalender-hijriah/${hy}/`), label: `Hijriah ${hy}H` },
        ],
      },
      {
        heading: locale === "en" ? "Popular Cities" : "Kota Populer",
        links: cities.slice(0, 6).map(([slug, name]) => ({
          to: `/jadwal-sholat/${slug}`,
          label: name,
        })),
      },
      {
        heading: locale === "en" ? "More Cities" : "Kota Lainnya",
        links: cities.slice(6).map(([slug, name]) => ({
          to: `/jadwal-sholat/${slug}`,
          label: name,
        })),
      },
      {
        heading: locale === "en" ? "More" : "Lainnya",
        links: [
          { to: href(`/kalender/${y}`), label: `Kalender ${y}` },
          { to: href("/hari"), label: locale === "en" ? "Notable Days" : "Hari Penting" },
          { to: href("/tools"), label: "Tools" },
          { to: "/zodiak/", label: "Zodiak" },
        ],
      },
    ];
  } else if (cluster === "tools") {
    title = "Tools & Kalkulator";
    columns = [
      {
        heading: "Hitung Tanggal",
        links: [
          { to: href("/tools/kalkulator-hari"), label: "Kalkulator Hari" },
          { to: href("/tools/hari-lahir"), label: "Hari Lahir" },
          { to: href("/tools/hari-jadian"), label: "Hari Jadian" },
          { to: href("/tools/hari-menikah"), label: "Lama Menikah" },
        ],
      },
      {
        heading: "Hubungan",
        links: [
          { to: href("/tools/hari-ldr"), label: "Lama LDR" },
          { to: href("/tools/hari-putus"), label: "Hari Putus" },
          { to: href("/tools/hari-sahabat"), label: "Lama Bersahabat" },
        ],
      },
      {
        heading: "Tradisi & Zodiak",
        links: [
          { to: href("/tools/cek-weton"), label: "Cek Weton" },
          { to: "/zodiak/", label: "Daftar Zodiak" },
          { to: "/zodiak/cocok/", label: "Kecocokan Zodiak" },
          { to: href("/tools"), label: "Semua Tools" },
        ],
      },
      {
        heading: locale === "en" ? "More" : "Lainnya",
        links: [
          { to: href(`/kalender/${y}`), label: `Kalender ${y}` },
          { to: href("/hari-ini"), label: locale === "en" ? "Today" : "Hari Ini" },
          { to: "/jadwal-sholat/", label: "Jadwal Sholat" },
          { to: href("/hari"), label: locale === "en" ? "Notable Days" : "Hari Penting" },
        ],
      },
    ];
  } else if (cluster === "important") {
    title = locale === "en" ? "Notable Days & Zodiac" : "Hari Penting & Zodiak";
    const thisMonth = getHariPentingForMonth(m).slice(0, 6);
    columns = [
      {
        heading: locale === "en" ? "This Month" : "Bulan Ini",
        links: thisMonth.length
          ? thisMonth.map((h) => ({ to: `/hari/${h.slug}`, label: h.name }))
          : [{ to: href("/hari"), label: locale === "en" ? "All Notable Days" : "Semua Hari Penting" }],
      },
      {
        heading: "Harpitnas",
        links: [
          { to: `/harpitnas/${y}/`, label: `Harpitnas ${y}` },
          { to: `/harpitnas/${y + 1}/`, label: `Harpitnas ${y + 1}` },
          { to: "/harpitnas/", label: locale === "en" ? "All Long Weekends" : "Daftar Harpitnas" },
        ],
      },
      {
        heading: "Zodiak",
        links: [
          { to: "/zodiak/", label: "12 Zodiak" },
          { to: "/zodiak/cocok/", label: "Kecocokan Zodiak" },
          { to: "/zodiak/aries/", label: "Aries" },
          { to: "/zodiak/leo/", label: "Leo" },
          { to: "/zodiak/scorpio/", label: "Scorpio" },
        ],
      },
      {
        heading: locale === "en" ? "More" : "Lainnya",
        links: [
          { to: href(`/kalender/${y}`), label: `Kalender ${y}` },
          { to: "/jadwal-sholat/", label: "Jadwal Sholat" },
          { to: href("/kalender-hijriah"), label: "Kalender Hijriah" },
          { to: href("/tools"), label: "Tools" },
        ],
      },
    ];
  }

  return (
    <section
      aria-label={title}
      className={`border-t ${theme.accentBorder} bg-muted/20`}
    >
      <div className="container mx-auto grid gap-6 px-4 py-8 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2 lg:col-span-4">
          <h2 className={`text-xs font-semibold uppercase tracking-wide ${theme.accentText}`}>
            {title}
          </h2>
        </div>
        {columns.map((col) => {
          const wide = col.links.length > 6;
          return (
            <div
              key={col.heading}
              className={wide ? "sm:col-span-2 lg:col-span-1" : ""}
            >
              <h3 className={`mb-2 text-sm font-semibold ${theme.accentText}`}>{col.heading}</h3>
              <ul
                className={
                  "text-sm " +
                  (wide
                    ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-x-3 gap-y-1"
                    : "space-y-1")
                }
              >
                {col.links.map((l) => (
                  <li key={l.to} className="min-w-0">
                    <Link
                      to={l.to as never}
                      className="block truncate text-muted-foreground hover:text-primary"
                      title={l.label}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}